import {TimeUtils} from '@colonist/utils'
import {GameColor} from '../../../../shared/scripts/GameColor'
import {GameFont} from '../../../../shared/scripts/GameFont'
import {SpriteView} from '../../../../shared/scripts/ui_canvas_engine/SpriteView'
import {TextView} from '../../../../shared/scripts/ui_canvas_engine/TextView'
import {OpacityAnimationWrapper} from '../../../../shared/scripts/ui_game/ui_game_animations/OpacityAnimationWrapper'
import {GameEndTimeAndTurnsView} from '../../../../shared/scripts/ui_game/ui_game_end_screen/GameEndTimeAndTurnsView'
import {HideableEndGameSection, UIGameEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndController'
import {UIGameEndFooterController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndFooterController'
import {UIGameEndItemBoxController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndItemBoxController'
import {UIWGameEndStatsController} from './UIWGameEndStatsController'
import {UIGameRankedEndController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameRankedEndController'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler'
import {Sprites} from '../../../../shared/scripts/ui_game/UIGameImageDictionary'
import {UIEndGameShareImage} from '../../../../shared/scripts/ui_share_image/UIEndGameShareImage'
import {UIGameEndShareButtonController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndShareButtonController'
import {ModeSettingEnum} from '@colonist/game-base-types'
import {ViewPopupShareImage} from '../../../../shared/scripts/ui_popup/ViewPopupShareImage'
import {UIEndGameTitleImage} from '../../../../shared/scripts/ui_share_image/UIEndGameTitleImage'
import {TranslationObject} from '@colonist/i18n'
import {UIGameEndReturnToMapButtonController} from '../../../../shared/scripts/ui_game/ui_game_end_screen/UIGameEndReturnToMapButtonController'

export class UIWGameEndController extends UIGameEndController {
    protected shareImage: UIEndGameShareImage
    protected titleImage: UIEndGameTitleImage | undefined

    protected extraBuildSteps() {
        this.createLogo()
        this.createEndgameItemBoxAd()
        this.animate()
        this.createShareImage()
    }

    protected createTitle() {
        const y = this.sectionSize * 0.85
        const text = this.getEndGameText()
        const color = GameColor.getColor(GameColor.Color.Gray3)

        this.title = new TextView(text, 0, y, this.contentContainer.width, this.sectionSize, this.contentContainer, GameFont.Size.XXXL, color)
        this.title.makeBold()
        this.title.centerXInParent()
    }

    private createLogo() {
        const logoPaddingTop = this.sectionSize / 10
        const ratio = 400 / 800
        const texture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.logo)
        this.logo = new SpriteView(0, logoPaddingTop, this.sectionSize / ratio, this.sectionSize, this.contentContainer, texture)
        this.logo.centerXInParent()
        this.logo.setClickAction(() => {
            this.footerController.handleExitingGame()
        })
    }

    protected createTimeAndTurns() {
        const paddingTop = this.sectionSize * 1.6
        this.timeTurns = new GameEndTimeAndTurnsView(0, paddingTop, this.contentContainer.width,
            this.sectionSize * 0.25, this.contentContainer, this.endGameState)
        this.timeTurns.timeAndTurnsTextView.makeBold()
    }

    protected createReturnToMapButton() {
        const canvas = this.uiGameManager.canvasController
        const w = canvas.itemSize * 2
        const h = canvas.itemSize / 1.5
        const x = canvas.appW - canvas.chatWidth - w * 1.06
        const y = h / 3
        const mapText: TranslationObject = {key: 'strings:game.endGame.cornerButton.map'}
        const mapWithReplayText: TranslationObject = {key: 'strings:game.endGame.cornerButton.mapWithReplay'}
        const returnToMapText = this.isReplayAvailable ? mapWithReplayText : mapText
        const statsText: TranslationObject = {key: 'strings:game.endGame.cornerButton.openStats'}
        this.returnToMapButton = new UIGameEndReturnToMapButtonController(this.uiGameManager, x, y, w, h, this, returnToMapText, statsText)
    }

    protected createShareButton() {
        if(!this.isSharingEnabled()) return

        const canvas = this.uiGameManager.canvasController
        const w = canvas.itemSize * 2
        const h = canvas.itemSize / 1.5
        const x = canvas.appW - canvas.chatWidth - w * 1.06
        // Gap of h/5, taking into account the position of previous buttons
        const y = h * 23 / 15
        this.shareButton = new UIGameEndShareButtonController(x, y, w, h, this.mainContainer, async() => this.shareButtonAction())
    }

    protected createStatsController() {
        const statsY = this.sectionSize * 2
        const statsH = this.sectionSize * 3
        this.statsController = new UIWGameEndStatsController(this.uiGameManager, this, statsY, statsH, this.contentContainer, this.endGameState)
    }

    protected createFooter() {
        const footerY = this.statsController.container.y + this.statsController.container.height
        const footerH = this.sectionSize * 1
        this.footerController = new UIGameEndFooterController(this.uiGameManager, footerY, footerH, this.mainContainer, this.winnerColor, this.isShownAfterLiveGame)
    }

    private createEndgameItemBoxAd() {
        if(!this.isShownAfterLiveGame) return

        const itemBoxPadding = 10
        const widestAreaInTop = Math.max(this.title.width, this.logo.width, this.timeTurns.timeAndTurnsTextView.width)
        const itemBoxWidth = (this.mainContainer.width / 2) - (widestAreaInTop / 2) - itemBoxPadding
        const itemBoxWidthIsPositive = itemBoxWidth > 0
        if(itemBoxWidthIsPositive) this.itemBoxController = new UIGameEndItemBoxController(this.uiGameManager, itemBoxWidth)
    }

    protected createEndgameRankedScreen() {
        const myRankedState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedState == undefined) return

        const viewsToHide: HideableEndGameSection[] = [this.statsController, this.shadeWithLine]
        new UIGameRankedEndController(this.uiGameManager, this.contentContainer, this.sectionSize, viewsToHide, this.footerController, myRankedState)
    }

    private createShareImage() {
        if(!this.isSharingEnabled()) return

        this.shareImage = new UIEndGameShareImage(this.uiGameManager, this.endGameState)

        if(this.endGamePlayerState?.title == undefined) return
        this.titleImage = new UIEndGameTitleImage(this.uiGameManager, this.endGamePlayerState.title, this.endGameState)
    }

    protected async shareButtonAction() {
        this.titleImage == undefined ? new ViewPopupShareImage(this.shareImage) : new ViewPopupShareImage(this.titleImage, this.shareImage, true)
    }

    protected isSharingEnabled(): boolean {
        const isBeginnerMode = this.uiGameManager.gameState.gameModeData.modeSetting == ModeSettingEnum.BeginnerModeClassic4P
        return !isBeginnerMode
    }

    protected animate() {
        if(this.uiGameManager.gameController.isReplay) return

        new OpacityAnimationWrapper(this.contentContainer, this.uiGameManager, 0, 1, 700)
        new OpacityAnimationWrapper(this.logo, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.title, this.uiGameManager, 0, 1, 300, 800)
        new OpacityAnimationWrapper(this.shadeWithLine.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.statsController.container, this.uiGameManager, 0, 1, 300, 2000)
        new OpacityAnimationWrapper(this.timeTurns, this.uiGameManager, 0, 1, 300, 4000)
        if(this.returnToMapButton != undefined) {
            new OpacityAnimationWrapper(this.returnToMapButton.button, this.uiGameManager, 0, 1, 300, 4000)
        }
        if(this.shareButton != undefined) {
            new OpacityAnimationWrapper(this.shareButton.button, this.uiGameManager, 0, 1, 300, 4000)
        }
        if(this.itemBoxController != undefined) {
            new OpacityAnimationWrapper(this.itemBoxController.itemBoxContainer, this.uiGameManager, 0, 1, 300, 4000)
        }
        const myRankedUserState = this.getPlayerRankedState(this.uiGameManager.gameController.myColor)
        if(myRankedUserState == undefined) {
            this.footerController.fadeInButtons(4 * TimeUtils.MILLISECONDS)
        }
    }
}
